

import React, { useState, useEffect } from 'react';
import {
  Typography, Button, TextField, CircularProgress, IconButton,
  Dialog, DialogActions, DialogContent, DialogTitle, Box, Paper, Alert
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { ThemeProvider } from '@mui/material/styles';
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, COLORS } from '../../styles/recruiterTheme';
import { Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import useActivityLogger from '../../hooks/useActivityLogger';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Page Wrapper ─────────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  font-family: ${FONT_BODY};
  background: #F3EEF9;
  min-height: 100vh;
  padding-bottom: 40px;
`;

// ─── Header ───────────────────────────────────────────────────────────────────
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 24px 32px;
  background: #fff;
  border-bottom: 1.5px solid #EDE7F6;
  box-shadow: 0 2px 12px rgba(79,55,139,0.07);
`;

const BackBtn = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 36px; height: 36px;
    transition: all 0.2s;
    flex-shrink: 0;
    &:hover {
      background: linear-gradient(135deg, #E8DEF8, #D0BCFF);
    }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: #8B7DB5 !important;
    font-weight: 500 !important;
    margin-top: 4px !important;
  }
`;

// ─── Content Layout ───────────────────────────────────────────────────────────
const ContentArea = styled.div`
  padding: 24px 32px;
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 24px;
  max-width: 1100px;
  animation: ${fadeSlideIn} 0.4s ease both;
  align-items: start;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
  @media (max-width: 600px) { padding: 16px; }
`;

// ─── Cards ────────────────────────────────────────────────────────────────────
const FormCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.lg}px !important;
    padding: 32px !important;
    box-shadow: 0 3px 20px rgba(79,55,139,0.09) !important;
    border: 1px solid #EDE7F6;
    background: #fff !important;
  }
`;

const SideCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.lg}px !important;
    padding: 24px !important;
    box-shadow: 0 3px 20px rgba(79,55,139,0.09) !important;
    border: 1px solid #EDE7F6;
    background: #fff !important;
    height: fit-content;
    position: sticky;
    top: 24px;
  }
`;

const SectionTitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.cardTitle}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
    margin-bottom: 16px !important;
    padding-bottom: 8px !important;
    border-bottom: 2px solid #F0EAF9;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

// ─── Info Row (side card) ─────────────────────────────────────────────────────
const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #FAF7FF;
  &:last-child { border-bottom: none; }
`;

const InfoKey = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  line-height: 20px;
  color: #8B7DB5;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  min-width: 96px;
  flex-shrink: 0;
`;

const InfoVal = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 600;
  line-height: 20px;
  color: #2D1B5E;
  min-width: 0;
  overflow-wrap: anywhere;
`;

// ─── Styled Fields ────────────────────────────────────────────────────────────
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${RADIUS.sm}px`,
    fontFamily: FONT_BODY,
    fontSize: TYPE.body,
    '&:hover fieldset': { borderColor: '#7C5CBF' },
    '&.Mui-focused fieldset': { borderColor: '#6750A4' },
    '&.Mui-disabled': { background: '#F8F4FF' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: FONT_BODY,
    fontSize: TYPE.body,
    '&.Mui-focused': { color: '#6750A4' },
  },
};

// ─── Interviewer Chip ─────────────────────────────────────────────────────────
const InterviewerChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #F3EEF9;
  border: 1.5px solid #D0BCFF;
  border-radius: ${RADIUS.sm}px;
  min-height: 40px;
  padding: 4px 8px 4px 12px;
  margin-bottom: 8px;
  transition: background 0.15s;
  &:hover { background: #EDE7F6; }
`;

const InterviewerEmail = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 600;
  color: #4F378B;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const DeleteBtn = styled(IconButton)`
  && {
    width: 32px; height: 32px;
    border-radius: ${RADIUS.sm}px;
    flex-shrink: 0;
    transition: all 0.18s;
    &:hover { background: #FDECEA; svg { color: #B3261E; } }
    svg { font-size: 18px; }
  }
`;

// ─── Primary Button ───────────────────────────────────────────────────────────
const PrimaryBtn = styled(Button)`
  && {
    background: linear-gradient(135deg, #6750A4, #4F378B) !important;
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.body}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 36px;
    padding: 0 16px !important;
    text-transform: none !important;
    box-shadow: 0 4px 14px rgba(79,55,139,0.28) !important;
    transition: all 0.2s !important;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 7px 20px rgba(79,55,139,0.38) !important;
    }
    &:disabled { opacity: 0.55 !important; transform: none !important; }
  }
`;

const SecondaryBtn = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.body}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 40px;
    padding: 0 16px !important;
    text-transform: none !important;
    border: 1.5px solid #C4B0E8 !important;
    background: transparent !important;
    transition: all 0.18s !important;
    &:hover { background: #EDE7F6 !important; }
  }
`;

// ─── Dialog ───────────────────────────────────────────────────────────────────
const SuccessIconWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const pad2 = (n) => String(n).padStart(2, '0');

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('rbotUser')) || {};
  } catch {
    return {};
  }
};

// ─────────────────────────────────────────────────────────────────────────────
const SchedulingPage = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state || {};
  const candidateEmail = props.candidateEmail ?? routeState.candidateEmail;
  const jdId = props.jdId ?? routeState.jdId;
  const candidateID = props.candidateID ?? routeState.candidateID;
  const companyName = props.companyName ?? routeState.companyName;
  const candidateName = props.candidateName ?? routeState.candidateName;
  const jobTitle = props.jobTitle ?? routeState.jobTitle;
  const recruiterEmail = props.recruiterEmail ?? routeState.recruiterEmail ?? getStoredUser().email;
  const { onBack, onScheduled } = props;
  const hasRequiredDetails = !!(candidateEmail && jdId && candidateID);
  const [interviewers, setInterviewers] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newInterviewer, setNewInterviewer] = useState('');
  const [open, setOpen] = useState(false);
  const [formError, setFormError] = useState('');

  const [availableSlots, setAvailableSlots] = useState([]);
const [loadingSlots, setLoadingSlots] = useState(false);
const [slotsError, setSlotsError] = useState(null);
const { log } = useActivityLogger(recruiterEmail, 'recruiter');

  const fetchinterviewersApiUrl = process.env.REACT_APP_FETCH_INTERVIEWERS_API;
  const scheduleApiUrl = process.env.REACT_APP_SCHEDULE_API;
  const screeningResultsApiUrl = process.env.REACT_APP_SCREENING_RESULTS_API;

  useEffect(() => {
    if (jdId) fetchInterviewers(jdId);
  }, [jdId]);

  const fetchInterviewers = async (jdId) => {
    try {
      const response = await axios.get(`${fetchinterviewersApiUrl}/${jdId}`);
      const interviewerEmails = response.data.interviewerEmails || [];
      setInterviewers(interviewerEmails);
    } catch (error) { console.error('Error fetching interviewers:', error); setInterviewers([]); }
  };

  // Local (not UTC) date/time, zero-padded so string comparisons work.
  const getCurrentDate = () => {
    const t = new Date();
    return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
  };
  const getCurrentTime = () => {
    const t = new Date();
    return `${pad2(t.getHours())}:${pad2(t.getMinutes())}`;
  };

  const handleDateChange = (e) => { setSelectedDate(e.target.value); setFormError(''); };
  const handleTimeChange = (e) => {
    const val = e.target.value;
    if (selectedDate === getCurrentDate() && val <= getCurrentTime()) {
      setFormError('You cannot select a time in the past. Please pick a future time.');
      return;
    }
    setFormError('');
    setSelectedTime(val);
  };

  useEffect(() => { setSelectedDate(getCurrentDate()); setSelectedTime(getCurrentTime()); }, []);

  const handleScheduling = async () => {
    if (loading) return;
    setFormError('');
    if (!selectedDate || !selectedTime) { setFormError('Please select both date and time.'); return; }
    const interviewDateTime = new Date(`${selectedDate}T${selectedTime}`);
    if (Number.isNaN(interviewDateTime.getTime()) || interviewDateTime <= new Date()) {
      setFormError('The interview date and time must be in the future.');
      return;
    }
    // Include an email typed in the field but not yet added with "+ Add".
    const pending = newInterviewer.trim();
    const updatedInterviewers = [...interviewers.map((e) => e.trim()), pending]
      .filter(Boolean)
      .filter((e, i, arr) => arr.indexOf(e) === i);
    if (updatedInterviewers.length === 0) {
      setFormError('Please add at least one interviewer email.');
      return;
    }
    const invalidEmails = updatedInterviewers.filter((e) => !EMAIL_RE.test(e));
    if (invalidEmails.length > 0) {
      setFormError(`Invalid interviewer email(s): ${invalidEmails.join(', ')}`);
      return;
    }
    try {
      setLoading(true);
      const screeningResultsResponse = await axios.get(`${screeningResultsApiUrl}/${candidateID}`);
      const screeningResults = screeningResultsResponse.data;
      const schedulingData = {
        jdId, recruiterEmail, candidateEmail,
        interviewerEmails: updatedInterviewers,
        candidateID,
        interview_time: interviewDateTime.toISOString(),
        job_title: jobTitle,
        candidate_name: candidateName,
        company_name: companyName,
        mcq_questions: screeningResults.mcq_questions || [],
        coding_questions: screeningResults.coding_questions || [],
      };
      const response = await axios.post(scheduleApiUrl, schedulingData);
      log('SCHEDULE_INTERVIEW_CONFIRMED', {
        jdId,
        targetName: candidateName,
        metadata: {
          candidateEmail,
          candidateID,
          interviewers:   updatedInterviewers,
          interviewDate:  selectedDate,
          interviewTime:  selectedTime,
          jobTitle,
          companyName,
        }
      });
      setInterviewers(updatedInterviewers);
      setNewInterviewer('');
      setIsScheduled(true);
      setOpen(true);
      setSelectedDate('');
      setSelectedTime('');
    } catch (error) {
      console.error('Error scheduling interview:', error);
      setFormError(
        error.response?.data?.error || error.response?.data?.message
        || 'Failed to schedule the interview. Please try again.'
      );
    }
    finally { setLoading(false); }
  };

  // useEffect(() => {
  //   if (selectedDate && interviewers.length > 0) {
  //     fetchAvailableSlots(interviewers[0], selectedDate);
  //   } else {
  //     setAvailableSlots([]);
  //   }
  // }, [selectedDate, interviewers]);

  // const fetchAvailableSlots = async (interviewerEmail, date) => {
  //   setLoadingSlots(true);
  //   setSlotsError(null);
  //   try {
  //     const response = await axios.get(
  //       `${process.env.REACT_APP_INTERVIEWER_AVAILABILITY_API}`,
  //       { params: { email: interviewerEmail, date } }
  //     );
  //     if (response.data.success) {
  //       setAvailableSlots(response.data.free_slots);
  //     } else {
  //       setSlotsError(response.data.error || 'Could not fetch slots');
  //     }
  //   } catch {
  //     setSlotsError('Failed to fetch interviewer availability');
  //   } finally {
  //     setLoadingSlots(false);
  //   }
  // };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  const handleAddInterviewer = () => {
    const email = newInterviewer.trim();
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      setFormError(`Invalid interviewer email: ${email}`);
      return;
    }
    if (interviewers.includes(email)) {
      setFormError(`${email} is already added.`);
      return;
    }
    setFormError('');
    log('ADD_INTERVIEWER_MANUALLY', {
      jdId,
      targetName: candidateName,
      metadata: { addedEmail: email }
    });
    setInterviewers([...interviewers, email]);
    setNewInterviewer('');
  };

  const handleDeleteInterviewer = (index) => {
    setInterviewers(interviewers.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    setOpen(false);
    const done = onScheduled || onBack;
    if (done) done();
    else navigate(-1);
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  if (!hasRequiredDetails) return (
    <ThemeProvider theme={recruiterTheme}>
    <PageWrapper>
      <HeaderBar>
        <BackBtn onClick={handleBack}><ArrowBackIcon /></BackBtn>
        <Box sx={{ minWidth: 0 }}>
          <PageTitle>Schedule Interview</PageTitle>
          <PageSubtitle>Set up an interview session for the candidate</PageSubtitle>
        </Box>
      </HeaderBar>
      <ContentArea>
        <FormCard sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: '40px !important' }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: COLORS.muted }}>
            Candidate details are missing. Please go back and open Schedule Interview from the candidate's page.
          </Typography>
          <SecondaryBtn onClick={handleBack}>Go Back</SecondaryBtn>
        </FormCard>
      </ContentArea>
    </PageWrapper>
    </ThemeProvider>
  );

  return (
    <ThemeProvider theme={recruiterTheme}>
    <PageWrapper>
      {/* Header */}
      <HeaderBar>
        <BackBtn onClick={handleBack}><ArrowBackIcon /></BackBtn>
        <Box sx={{ minWidth: 0 }}>
          <PageTitle>Schedule Interview</PageTitle>
          <PageSubtitle>Set up an interview session for the candidate</PageSubtitle>
        </Box>
      </HeaderBar>

      <ContentArea>
        {/* ── Left: Form ── */}
        <FormCard>

          {/* Candidate Info */}
          <SectionTitle>
            <PersonIcon sx={{ fontSize: 18, color: '#6750A4' }} />
            Candidate Details
          </SectionTitle>

          <TextField
            label="Candidate Email"
            value={candidateEmail || ''}
            disabled
            fullWidth
            size="small"
            sx={{ ...fieldSx, mb: 3 }}
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, fontSize: 18, color: '#8B7DB5' }} />,
            }}
          />

          {/* Interviewers */}
          <SectionTitle>
            <GroupAddIcon sx={{ fontSize: 18, color: '#6750A4' }} />
            Interviewers
          </SectionTitle>

          {interviewers.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {interviewers.map((interviewer, index) => (
                <InterviewerChip key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <PersonIcon sx={{ fontSize: 16, color: '#6750A4', flexShrink: 0 }} />
                    <InterviewerEmail title={interviewer}>{interviewer}</InterviewerEmail>
                  </Box>
                  <DeleteBtn onClick={() => handleDeleteInterviewer(index)} size="small">
                    <DeleteIcon />
                  </DeleteBtn>
                </InterviewerChip>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center' }}>
            <TextField
              label="Add Interviewer Email"
              value={newInterviewer}
              onChange={(e) => { setNewInterviewer(e.target.value); setFormError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddInterviewer(); } }}
              sx={{ ...fieldSx, flex: 1 }}
              size="small"
            />
            <SecondaryBtn onClick={handleAddInterviewer} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              + Add
            </SecondaryBtn>
          </Box>

          {/* Date & Time */}
          <SectionTitle>
            <CalendarTodayIcon sx={{ fontSize: 18, color: '#6750A4' }} />
            Interview Schedule
          </SectionTitle>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <TextField
              label="Interview Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              sx={fieldSx}
              onChange={handleDateChange}
              inputProps={{ min: getCurrentDate() }}
              value={selectedDate}
              required
              InputProps={{
                startAdornment: <CalendarTodayIcon sx={{ mr: 1, fontSize: 18, color: '#8B7DB5' }} />,
              }}
            />
            <TextField
              label="Interview Time"
              type="time"
              InputLabelProps={{ shrink: true }}
              value={selectedTime}
              onChange={handleTimeChange}
              fullWidth
              required
              size="small"
              sx={fieldSx}
              inputProps={selectedDate === getCurrentDate() ? { min: getCurrentTime() } : {}}
              InputProps={{
                startAdornment: <AccessTimeIcon sx={{ mr: 1, fontSize: 18, color: '#8B7DB5' }} />,
              }}
            />

            {/* Replace the time TextField with this */}
{/* <Box>
  <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 12, fontWeight: 700, color: '#8B7DB5', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}>
    Available Slots
  </Typography>

  {loadingSlots && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress size={16} sx={{ color: '#6750A4' }} />
      <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 12, color: '#8B7DB5' }}>
        Checking availability…
      </Typography>
    </Box>
  )}

  {slotsError && (
    <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 12, color: '#E65100' }}>
      {slotsError} — enter time manually below
    </Typography>
  )}

  {!loadingSlots && availableSlots.length > 0 && (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      {availableSlots.map((slot, i) => (
        <Box
          key={i}
          onClick={() => setSelectedTime(slot.start)}
          sx={{
            padding: '7px 14px',
            borderRadius: '10px',
            border: `1.5px solid ${selectedTime === slot.start ? '#6750A4' : '#D0BCFF'}`,
            background: selectedTime === slot.start
              ? 'linear-gradient(135deg, #6750A4, #4F378B)'
              : '#F8F4FF',
            color: selectedTime === slot.start ? '#fff' : '#4F378B',
            fontFamily: 'Plus Jakarta Sans',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 0.15s',
            '&:hover': {
              background: selectedTime === slot.start
                ? 'linear-gradient(135deg, #6750A4, #4F378B)'
                : '#EDE7F6',
            }
          }}
        >
          {slot.label}
        </Box>
      ))}
    </Box>
  )}

  {!loadingSlots && availableSlots.length === 0 && selectedDate && interviewers.length > 0 && !slotsError && (
    <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: 12, color: '#B3261E', mb: 1 }}>
      No free slots found — interviewer may be fully booked
    </Typography>
  )} */}

  {/* Manual time input as fallback */}
  {/* <TextField
    label="Or enter time manually"
    type="time"
    InputLabelProps={{ shrink: true }}
    value={selectedTime}
    onChange={handleTimeChange}
    size="small"
    sx={{ ...fieldSx, width: '100%' }}
    InputProps={{
      startAdornment: <AccessTimeIcon sx={{ mr: 1, fontSize: 16, color: '#8B7DB5' }} />,
    }}
  />
</Box> */}
          </Box>

          {formError && (
            <Alert severity="error" onClose={() => setFormError('')} sx={{ mb: 2, borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY }}>
              {formError}
            </Alert>
          )}

          {/* Schedule Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <PrimaryBtn
              onClick={handleScheduling}
              disabled={loading}
            >
              {loading ? <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> : null}
              {loading ? 'Scheduling…' : '📅 Schedule Interview'}
            </PrimaryBtn>
          </Box>
        </FormCard>

        {/* ── Right: Summary Card ── */}
        <SideCard>
          <SectionTitle>
            <WorkIcon sx={{ fontSize: 18, color: '#6750A4' }} />
            Interview Summary
          </SectionTitle>

          {candidateName && (
            <InfoRow>
              <InfoKey>Candidate</InfoKey>
              <InfoVal>{candidateName}</InfoVal>
            </InfoRow>
          )}
          {candidateEmail && (
            <InfoRow>
              <InfoKey>Email</InfoKey>
              <InfoVal>{candidateEmail}</InfoVal>
            </InfoRow>
          )}
          {jobTitle && (
            <InfoRow>
              <InfoKey>Role</InfoKey>
              <InfoVal>{jobTitle}</InfoVal>
            </InfoRow>
          )}
          {companyName && (
            <InfoRow>
              <InfoKey>Company</InfoKey>
              <InfoVal>{companyName}</InfoVal>
            </InfoRow>
          )}
          {selectedDate && (
            <InfoRow>
              <InfoKey>Date</InfoKey>
              <InfoVal>{new Date(`${selectedDate}T00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</InfoVal>
            </InfoRow>
          )}
          {selectedTime && (
            <InfoRow>
              <InfoKey>Time</InfoKey>
              <InfoVal>{selectedTime}</InfoVal>
            </InfoRow>
          )}
          {interviewers.length > 0 && (
            <InfoRow>
              <InfoKey>Interviewers</InfoKey>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {interviewers.map((iv, i) => (
                  <InfoVal key={i} style={{ fontSize: TYPE.caption }}>{iv}</InfoVal>
                ))}
              </Box>
            </InfoRow>
          )}

          {/* Visual checklist */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '2px solid #F0EAF9' }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.overline, color: '#8B7DB5', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
              Checklist
            </Typography>
            {[
              { label: 'Candidate email',    done: !!candidateEmail },
              { label: 'At least 1 interviewer', done: interviewers.length > 0 },
              { label: 'Date selected',      done: !!selectedDate },
              { label: 'Time selected',      done: !!selectedTime },
            ].map(({ label, done }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: done ? '#2D7D46' : '#C4B0E8' }} />
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: done ? '#2D1B5E' : '#B0A0D0' }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </SideCard>
      </ContentArea>

      {/* ── Success Dialog ── */}
      <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px`, padding: '8px', minWidth: 360 } }}>
        <DialogTitle sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25, color: '#2D1B5E', textAlign: 'center', pt: 3 }}>
          <SuccessIconWrap>
            <CheckCircleIcon sx={{ fontSize: 56, color: '#2D7D46' }} />
          </SuccessIconWrap>
          Interview Scheduled!
        </DialogTitle>
        <DialogContent>
          {isScheduled && (
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: '#49454F', textAlign: 'center', lineHeight: 1.5 }}>
              The interview has been scheduled successfully. You can view it in the dashboard under Scheduled Interviews.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <PrimaryBtn onClick={handleClose} sx={{ minWidth: 120 }}>Close</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </PageWrapper>
    </ThemeProvider>
  );
};

export default SchedulingPage;